/**
 * F5 product page behaviour: variant selection, price/availability updates,
 * quantity stepper, and image gallery switching. Dependency-free.
 */
(function () {
  class F5ProductForm extends HTMLElement {
    connectedCallback() {
      this.variants = this.safeParse('[data-variants]');
      this.priceMap = this.safeParse('[data-variant-prices]') || {};

      this.optionInputs = Array.from(this.querySelectorAll('[data-product-option]'));
      this.idInput = this.querySelector('[data-variant-id]');
      this.priceEl = this.querySelector('[data-price]');
      this.compareEl = this.querySelector('[data-compare-price]');
      this.addButton = this.querySelector('[data-add-button]');
      this.addButtonText = this.querySelector('[data-add-button-text]');

      this.optionInputs.forEach((input) => {
        input.addEventListener('change', () => this.onOptionChange());
      });

      this.refreshAvailabilityFromProductJson();

      // AJAX add-to-cart: a plain form POST is intercepted by the password
      // gate on preview stores and gives no inline error handling in
      // production, so submit through the cart API and then go to the cart.
      this.form = this.querySelector('form[action*="/cart/add"]');
      if (this.form) {
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
      }

      // Dynamic checkout (Buy it now) cannot carry a selling plan; hide it
      // while a subscription option is selected.
      this.querySelectorAll('input[name="selling_plan"]').forEach((radio) => {
        radio.addEventListener('change', () => this.togglePaymentButton());
      });
      this.togglePaymentButton();
    }

    togglePaymentButton() {
      const paymentButton = this.querySelector('.shopify-payment-button');
      if (!paymentButton) return;
      const selected = this.querySelector('input[name="selling_plan"]:checked');
      paymentButton.style.display = selected && selected.value ? 'none' : '';
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.addButton && this.addButton.disabled) return;

      const restoreLabel = this.addButtonText ? this.addButtonText.textContent : '';
      if (this.addButtonText) this.addButtonText.textContent = 'Adding\u2026';
      if (this.addButton) this.addButton.disabled = true;

      const cartDrawer = document.querySelector('cart-drawer');
      const formData = new FormData(this.form);
      if (cartDrawer) {
        // No sections_url: the drawer sections are context-independent, and
        // rendering them against a product URL returns null sections.
        formData.append('sections', 'cart-drawer,cart-icon-bubble');
      }

      fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status) {
            throw new Error(data.description || 'Could not add to cart');
          }
          if (this.addButton) this.addButton.disabled = false;
          if (this.addButtonText) this.addButtonText.textContent = restoreLabel;
          const drawerHtml = data.sections && data.sections['cart-drawer'];
          const bubbleHtml = data.sections && data.sections['cart-icon-bubble'];
          if (cartDrawer && drawerHtml && bubbleHtml) {
            // Same slide-in behavior as the homepage instead of a redirect.
            cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents({
              id: data.variant_id || data.id,
              sections: { 'cart-drawer': drawerHtml, 'cart-icon-bubble': bubbleHtml },
            });
          } else {
            // Item is already added; the cart page is a safe landing spot if
            // the bundled section render was unavailable.
            window.location.href = '/cart';
          }
        })
        .catch((error) => {
          if (this.addButton) this.addButton.disabled = false;
          if (this.addButtonText) this.addButtonText.textContent = restoreLabel;
          this.showFormError(error.message);
        });
    }

    showFormError(message) {
      let errorEl = this.querySelector('[data-form-error]');
      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.setAttribute('data-form-error', '');
        errorEl.setAttribute('role', 'alert');
        errorEl.style.cssText = 'color:#E63946;font-size:0.85rem;margin:8px 0 0;';
        this.form.appendChild(errorEl);
      }
      errorEl.textContent = message;
    }

    safeParse(selector) {
      const el = this.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    refreshAvailabilityFromProductJson() {
      fetch(`${window.location.pathname}.js`, {
        headers: { Accept: 'application/json' },
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((product) => {
          if (!product || !Array.isArray(product.variants)) return;

          this.variants = (this.variants || []).map((variant) => {
            const freshVariant = product.variants.find((item) => item.id === variant.id);
            return freshVariant ? { ...variant, available: freshVariant.available } : variant;
          });

          const selectedVariantId = Number(this.idInput && this.idInput.value);
          const selectedVariant =
            product.variants.find((variant) => variant.id === selectedVariantId) ||
            product.variants.find((variant) => variant.available);

          if (!selectedVariant) return;
          if (this.idInput) this.idInput.value = selectedVariant.id;
          this.setAvailability(selectedVariant.available);
        })
        .catch(() => {});
    }

    get selectedOptions() {
      return this.optionInputs.map((input) => input.value);
    }

    onOptionChange() {
      const selected = this.selectedOptions;
      const variant = (this.variants || []).find((v) =>
        v.options.every((option, index) => option === selected[index])
      );

      this.updateVariant(variant);
    }

    updateVariant(variant) {
      if (!variant) {
        this.setUnavailable();
        return;
      }

      if (this.idInput) this.idInput.value = variant.id;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      const data = this.priceMap[variant.id];
      if (data && this.priceEl) {
        this.priceEl.innerHTML = data.price;
      }
      if (this.compareEl) {
        if (data && data.compare_html && data.on_sale) {
          this.compareEl.innerHTML = data.compare_html;
          this.compareEl.hidden = false;
        } else {
          this.compareEl.hidden = true;
        }
      }

      if (this.priceEl) {
        this.priceEl.classList.toggle('price--on-sale', !!(data && data.on_sale));
      }

      this.setAvailability(variant.available);
    }

    setAvailability(available) {
      if (!this.addButton) return;
      this.addButton.disabled = !available;
      if (this.addButtonText) {
        this.addButtonText.textContent = available
          ? this.addButton.dataset.labelDefault
          : this.addButton.dataset.labelSoldOut;
      }
    }

    setUnavailable() {
      if (!this.addButton) return;
      this.addButton.disabled = true;
      if (this.addButtonText) {
        this.addButtonText.textContent = this.addButton.dataset.labelUnavailable;
      }
    }
  }

  if (!customElements.get('f5-product-form')) {
    customElements.define('f5-product-form', F5ProductForm);
  }

  class F5QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const step = button.dataset.action === 'increase' ? 1 : -1;
          const min = parseInt(this.input.min, 10) || 1;
          const current = parseInt(this.input.value, 10) || min;
          const next = Math.max(min, current + step);
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }

  if (!customElements.get('f5-quantity-input')) {
    customElements.define('f5-quantity-input', F5QuantityInput);
  }

  class F5ProductGallery extends HTMLElement {
    connectedCallback() {
      this.mainImage = this.querySelector('[data-gallery-main] img');
      this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const full = thumb.dataset.full;
          const alt = thumb.dataset.alt || '';
          if (this.mainImage && full) {
            this.mainImage.src = full;
            this.mainImage.srcset = '';
            this.mainImage.alt = alt;
          }
          this.querySelectorAll('[data-gallery-thumb]').forEach((t) =>
            t.classList.remove('is-active')
          );
          thumb.classList.add('is-active');
        });
      });
    }
  }

  if (!customElements.get('f5-product-gallery')) {
    customElements.define('f5-product-gallery', F5ProductGallery);
  }
})();
